import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "";

interface Attempt {
  id: string;
  personalCode: string;
  givenName: string | null;
  familyName: string | null;
  result: "allowed" | "denied" | "guest" | "revoked";
  ipAddress: string | null;
  createdAt: string;
}

type ResultFilter = "all" | "allowed" | "guest" | "denied" | "revoked";
type DateFilter = "24h" | "7d" | "30d";

export function LoginAttemptsTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState<Attempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [resultFilter, setResultFilter] = useState<ResultFilter>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("7d");

  const fetchAttempts = useCallback(async () => {
    setLoading(true);
    try {
      const ms =
        dateFilter === "24h" ? 24 * 3600_000 :
        dateFilter === "7d"  ? 7 * 24 * 3600_000 :
                               30 * 24 * 3600_000;
      const from = new Date(Date.now() - ms).toISOString();

      const qs = new URLSearchParams({ page: String(page), limit: "20", dateFrom: from });
      if (resultFilter !== "all") qs.set("result", resultFilter);

      const res = await axios.get<{ success: boolean; data: Attempt[]; pagination: any }>(
        `${API_URL}/api/admin/login-attempts?${qs.toString()}`,
        { withCredentials: true }
      );
      if (res.data.success) {
        setItems(res.data.data);
        setPages(res.data.pagination.pages);
      }
    } finally {
      setLoading(false);
    }
  }, [page, resultFilter, dateFilter]);

  useEffect(() => { fetchAttempts(); }, [fetchAttempts]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Select value={resultFilter} onValueChange={(v) => { setPage(1); setResultFilter(v as ResultFilter); }}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.loginAttempts.filterAll")}</SelectItem>
            <SelectItem value="allowed">{t("admin.loginAttempts.filterAllowed")}</SelectItem>
            <SelectItem value="guest">{t("admin.loginAttempts.filterGuest")}</SelectItem>
            <SelectItem value="denied">{t("admin.loginAttempts.filterDenied")}</SelectItem>
            <SelectItem value="revoked">{t("admin.loginAttempts.filterRevoked")}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={dateFilter} onValueChange={(v) => { setPage(1); setDateFilter(v as DateFilter); }}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">{t("admin.loginAttempts.filter24h")}</SelectItem>
            <SelectItem value="7d">{t("admin.loginAttempts.filter7d")}</SelectItem>
            <SelectItem value="30d">{t("admin.loginAttempts.filter30d")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.loginAttempts.colTime")}</TableHead>
              <TableHead>{t("admin.loginAttempts.colCode")}</TableHead>
              <TableHead>{t("admin.loginAttempts.colName")}</TableHead>
              <TableHead>{t("admin.loginAttempts.colResult")}</TableHead>
              <TableHead>{t("admin.loginAttempts.colIp")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-center py-6">
                <Loader2 className="w-5 h-5 animate-spin inline" />
              </TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">—</TableCell></TableRow>
            )}
            {!loading && items.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{new Date(a.createdAt).toLocaleString()}</TableCell>
                <TableCell className="font-mono">{a.personalCode}</TableCell>
                <TableCell>
                  {[a.givenName, a.familyName].filter(Boolean).join(" ") || "—"}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      a.result === "allowed"
                        ? "default"
                        : a.result === "guest"
                          ? "secondary"
                          : a.result === "revoked"
                            ? "outline"
                            : "destructive"
                    }>
                    {a.result === "allowed"
                      ? t("admin.loginAttempts.resultAllowed")
                      : a.result === "guest"
                        ? t("admin.loginAttempts.resultGuest")
                        : a.result === "revoked"
                          ? t("admin.loginAttempts.resultRevoked")
                          : t("admin.loginAttempts.resultDenied")}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">{a.ipAddress ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <Button
          variant="outline" size="sm"
          disabled={page <= 1} onClick={() => setPage(page - 1)}
        >
          <ChevronLeft className="w-4 h-4" /> {t("common.previous")}
        </Button>
        <div className="text-sm text-muted-foreground">
          {t("common.page")} {page} / {pages}
        </div>
        <Button
          variant="outline" size="sm"
          disabled={page >= pages} onClick={() => setPage(page + 1)}
        >
          {t("common.next")} <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
